import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'src/app/models/menu-item';
import { MenuItemService } from 'src/app/services/menu-item.service';

interface SearchScreen {
  name: string;       // nome exibido (label traduzido)
  path: string;       // caminho no menu (grupos/seções ancestrais)
  routerLink: string; // rota de navegação
  aliases: string[];  // apelidos/palavras-chave (vindos do backend)
}

interface SearchResult extends SearchScreen {
  aliasHit?: string;  // apelido que casou (quando o nome não casou)
}

/**
 * Busca global do EasyWeb (EAS-263). Atalho de navegação: abre por ícone no header ou Ctrl/⌘+K,
 * lista as telas do menu (filtradas pela visibilidade do usuário) que casam por nome ou apelido,
 * e só redireciona — nunca executa ação.
 */
@Component({
  selector: 'app-header-search',
  templateUrl: './header-search.component.html',
  styleUrls: ['./header-search.component.scss'],
})
export class HeaderSearchComponent implements OnInit {

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  open = false;
  query = '';
  results: SearchResult[] = [];
  focused = 0;

  private screens: SearchScreen[] = [];
  private indexLoaded = false;

  constructor(
    private router: Router,
    private translate: TranslateService,
    private menuItemService: MenuItemService,
  ) {}

  ngOnInit(): void {
    this.loadIndex();
  }

  // Ctrl+K / ⌘+K abre/fecha de qualquer tela, mesmo com foco em campo de texto (CA-BUS-02).
  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.toggle();
      return;
    }
    if (!this.open) {
      return;
    }
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (this.results.length) {
          this.focused = (this.focused + 1) % this.results.length;
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.results.length) {
          this.focused = (this.focused - 1 + this.results.length) % this.results.length;
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (this.results.length) {
          this.go(this.results[this.focused]);
        }
        break;
      default:
        break;
    }
  }

  toggle(): void {
    if (this.open) {
      this.close();
    } else {
      this.openSearch();
    }
  }

  openSearch(): void {
    // Recarrega o índice ao abrir para refletir troca de marina / atualização do menu.
    this.loadIndex();
    this.open = true;
    this.query = '';
    this.results = [];
    this.focused = 0;
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
  }

  close(): void {
    this.open = false;
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('search-backdrop')) {
      this.close();
    }
  }

  onInput(): void {
    this.focused = 0;
    this.results = this.compute(this.query);
  }

  setFocused(index: number): void {
    this.focused = index;
  }

  go(screen: SearchResult): void {
    this.close();
    const url = (screen.routerLink || '').replace(/^#/, '');
    if (url) {
      this.router.navigateByUrl(url);
    }
  }

  private loadIndex(): void {
    this.menuItemService.getFilteredStructured().subscribe({
      next: (menu) => {
        this.screens = [];
        this.buildIndex(menu || [], []);
        this.indexLoaded = true;
        if (this.open && this.query) {
          this.results = this.compute(this.query);
        }
      },
      error: () => {
        this.indexLoaded = true;
      },
    });
  }

  // Achata a árvore: cada nó com rota navegável vira uma tela; o caminho são os labels ancestrais.
  private buildIndex(items: MenuItem[], ancestors: string[]): void {
    for (const item of items || []) {
      const name = this.translate.instant(item.label);
      const link = item.routerLink || '';
      const isNavigable = !!link && !link.startsWith('#');
      if (isNavigable) {
        this.screens.push({
          name,
          path: ancestors.join(' › '),
          routerLink: link,
          aliases: item.aliases || [],
        });
      }
      if (item.children && item.children.length) {
        this.buildIndex(item.children, [...ancestors, name]);
      }
    }
  }

  private compute(query: string): SearchResult[] {
    const q = this.norm((query || '').trim());
    if (!q) {
      return [];
    }
    const out: SearchResult[] = [];
    for (const screen of this.screens) {
      const nameHit = this.norm(screen.name).includes(q);
      let aliasHit: string;
      if (!nameHit) {
        aliasHit = (screen.aliases || []).find((a) => this.norm(a).includes(q));
      }
      if (nameHit || aliasHit) {
        out.push({ ...screen, aliasHit });
      }
    }
    return out;
  }

  // Normaliza para casar ignorando caixa e acento (CA-BUS-06).
  private norm(value: string): string {
    return (value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
}